import * as d3 from 'd3';
import { ChartDataArray } from '../data/ChartDataArray';
import { ChartRenderer } from './ChartRenderer';
import { RendererConfig } from './RendererConfig';
import { LegendRenderer } from './LegendRenderer';
import { Rectangle } from './Rectange';
import { LinearAxisRenderer } from './LineAxisChartRenderer';
import { BandAxisRenderer } from './BandAxisRenderer';
import { DataRenderer } from './DataRenderer';
import { HighlightRenderer } from './HighlightRenderer';
import { ChartDataType } from '../data/ChartDataType';
import { TooltipRenderer } from './TooltipRenderer';
import { ChartRendererContext } from './ChartRendererContext';
import { BandChartConfig } from 'components/chart/data/BandChartConfig';
import { BandRenderer } from 'components/chart/renderer/BandRenderer';
import { DetectionDateMarkerRenderer } from './DetectionDateMarkerRenderer';

export class BandChartRenderer extends ChartRenderer {
  private data: ChartDataArray[] = [];

  private rerenderConfig?: RendererConfig;

  constructor(protected config: BandChartConfig) {
    super();
  }

  render(el: HTMLElement, context: ChartRendererContext) {
    this.data = this.config.data!;
    this.rerenderConfig = new RendererConfig(this.config);

    const svg = d3
      .select(el)
      .append('svg')
      .attr('class', 'mnb-chart mnb-line-chart line-chart')
      .attr('width', '100%')
      .attr('height', '100%');

    const dimensions: { height: number; width: number } = ChartRenderer.getDimensions(el);

    let legendHeight = 0;

    if (!this.config.legendConfig.hide) {
      legendHeight = new LegendRenderer(context, this.config).render(svg, this.data, dimensions);
    }

    const chartRect: Rectangle = {
      x: 0,
      y: 5,
      width: dimensions.width - 1,
      height: dimensions.height - 5 - legendHeight,
    };

    const lineChartRect: Rectangle = {
      x: chartRect.x,
      y: chartRect.y,
      width: chartRect.width,
      height: chartRect.height,
    };

    this.gElement = svg.append('g');

    const yAxisRenderer = new LinearAxisRenderer(
      this.gElement,
      context,
      this.config,
      false,
      this.rerenderConfig.hasSecondaryAxis()
    );
    const xAxisRenderer = new BandAxisRenderer(this.gElement, context, this.config, false);

    const xAxisHeight = xAxisRenderer.calcXHeight();
    const innerHeight = lineChartRect.height - xAxisHeight;

    const yAxis = yAxisRenderer.renderY(lineChartRect.width, innerHeight);

    let secondaryYAxis: {
      scale: d3.ScaleLinear<number, number>;
      width: number;
    } | null = null;

    if (this.rerenderConfig.hasSecondaryAxis()) {
      const secondaryYAxisRenderer = new LinearAxisRenderer(this.gElement, context, this.config, true, true);

      secondaryYAxis = secondaryYAxisRenderer.renderY(lineChartRect.width - yAxis.width, innerHeight);
    }

    const innerRect: Rectangle = {
      x: lineChartRect.x + yAxis.width,
      y: lineChartRect.y,
      width:
        lineChartRect.width -
        yAxis.width -
        (secondaryYAxis ? secondaryYAxis.width : 0) -
        (this.config.removeOuterSpacing ? 5 : 0),
      height: innerHeight,
    };

    this.gElement.attr('transform', `translate(${innerRect.x},${innerRect.y})`);

    const xAxis = xAxisRenderer.renderX(innerRect.width, innerRect.height);

    const dataRenderers: DataRenderer[] = [];

    // Draw only if has more than one line or xAxis is not hidden
    if (this.config.data!.length > 1 || !this.config.xAxisConfig.hide) {
      // draw highlight lines here so that they are rendered beneath the data lines, guides, etc
      const highlightRenderer = new HighlightRenderer(
        this.gElement,
        this.config.removeOuterSpacing,
        this.config.highlightLineColor
      );
      highlightRenderer.render(xAxis.scale, yAxis.scale);
      dataRenderers.push(highlightRenderer);
    }

    const lineRenderer = new BandRenderer(
      this.gElement!,
      this.config.getDataArray(ChartDataType.breakdown)!,
      this.config.gradientCreator!
    );

    if (lineRenderer) {
      // render lines in reverse order -> lines that were added to
      // lineRenderers first will have the highest "z-index"
      lineRenderer.render(xAxis.scale, !lineRenderer.secondaryAxis ? yAxis.scale : secondaryYAxis!.scale);
      lineRenderer.renderGuides(xAxis.scale, !lineRenderer.secondaryAxis ? yAxis.scale : secondaryYAxis!.scale);
    }

    if (this.config.detectionDateMarker) {
      const primarySeries = this.config.getDataArray(ChartDataType.primary);
      if (primarySeries) {
        const detectionDateMarker = new DetectionDateMarkerRenderer(
          this.gElement,
          {
            color: this.config.detectionDateMarker.color,
            focusRingColor: this.config.detectionDateMarker.focusRingColor,
          },
          this.config.removeOuterSpacing
        );
        detectionDateMarker.render(xAxis.scale, yAxis.scale, primarySeries);
        dataRenderers.push(detectionDateMarker);
      }
    }

    new TooltipRenderer(
      this.config,
      this.tooltipContent!,
      [lineRenderer, ...dataRenderers],
      this.config.breakdownMode === 'parallel',
      this.config.removeOuterSpacing
    ).render(svg, innerRect, xAxis.scale, yAxis.scale, undefined, false);
  }
}
